/**
 * Dynamic-programming line solver for nonogram uniqueness checks.
 * A slice is accepted only if every cell can be deduced by line propagation
 * (no branching / backtracking required for casual play).
 */

function rleClue(line) {
    const out = [];
    let c = 0;
    for (const v of line) {
        if (v === 1) c++;
        else if (c) {
            out.push(c);
            c = 0;
        }
    }
    if (c) out.push(c);
    return out.length ? out : [0];
}

/** All placements of clue blocks into length n; returns array of binary lines (0/1). */
function placements(clue, n) {
    const blocks = clue[0] === 0 && clue.length === 1 ? [] : clue;
    const results = [];
    function rec(idx, pos, line) {
        if (idx === blocks.length) {
            results.push(line.slice());
            return;
        }
        const len = blocks[idx];
        const remaining = blocks.slice(idx + 1).reduce((a, b) => a + b, 0);
        const minGaps = blocks.length - idx - 1; // at least 1 gap between remaining blocks
        const maxStart = n - remaining - minGaps - len;
        for (let start = pos; start <= maxStart; start++) {
            const next = line.slice();
            for (let i = start; i < start + len; i++) next[i] = 1;
            rec(idx + 1, start + len + 1, next);
        }
    }
    rec(0, 0, Array(n).fill(0));
    return results;
}

function consistent(placement, known) {
    for (let i = 0; i < known.length; i++) {
        if (known[i] === null) continue;
        if (known[i] !== placement[i]) return false;
    }
    return true;
}

function intersectPlacements(pls) {
    if (!pls.length) return null;
    const n = pls[0].length;
    const out = Array(n).fill(null);
    for (let i = 0; i < n; i++) {
        const v0 = pls[0][i];
        if (pls.every((p) => p[i] === v0)) out[i] = v0;
    }
    return out;
}

/**
 * Line-solve a binary grid given row/col clues.
 * Returns { solvable, grid } where grid cells are 0/1 if uniquely deduced.
 */
export function lineSolve(rowsClues, colsClues, maxIter = 64) {
    const h = rowsClues.length;
    const w = colsClues.length;
    const known = Array.from({ length: h }, () => Array(w).fill(null));

    for (let iter = 0; iter < maxIter; iter++) {
        let changed = false;
        for (let r = 0; r < h; r++) {
            const lineKnown = known[r];
            const pls = placements(rowsClues[r], w).filter((p) => consistent(p, lineKnown));
            const inter = intersectPlacements(pls);
            if (!inter) return { solvable: false, grid: null };
            for (let c = 0; c < w; c++) {
                if (inter[c] !== null && known[r][c] === null) {
                    known[r][c] = inter[c];
                    changed = true;
                }
            }
        }
        for (let c = 0; c < w; c++) {
            const lineKnown = known.map((row) => row[c]);
            const pls = placements(colsClues[c], h).filter((p) => consistent(p, lineKnown));
            const inter = intersectPlacements(pls);
            if (!inter) return { solvable: false, grid: null };
            for (let r = 0; r < h; r++) {
                if (inter[r] !== null && known[r][c] === null) {
                    known[r][c] = inter[r];
                    changed = true;
                }
            }
        }
        if (!changed) break;
    }

    for (let r = 0; r < h; r++) {
        for (let c = 0; c < w; c++) {
            if (known[r][c] === null) return { solvable: false, grid: null };
        }
    }
    return { solvable: true, grid: known };
}

export function cluesFromGrid(grid) {
    const rows = grid.map((row) => rleClue(row.map((v) => (v ? 1 : 0))));
    const cols = [];
    const w = grid[0].length;
    for (let c = 0; c < w; c++) {
        const col = grid.map((row) => (row[c] ? 1 : 0));
        cols.push(rleClue(col));
    }
    return { rows, cols };
}

export function isNonTrivial(grid) {
    const flat = grid.flat().map((v) => (v ? 1 : 0));
    const fill = flat.reduce((a, b) => a + b, 0) / flat.length;
    return fill >= 0.15 && fill <= 0.85;
}

/**
 * Validate that a solution grid is uniquely line-solvable from its own clues.
 */
export function isUniquelyLineSolvable(grid) {
    if (!isNonTrivial(grid)) return false;
    const { rows, cols } = cluesFromGrid(grid);
    const res = lineSolve(rows, cols);
    if (!res.solvable) return false;
    for (let r = 0; r < grid.length; r++) {
        for (let c = 0; c < grid[0].length; c++) {
            if ((res.grid[r][c] ? 1 : 0) !== (grid[r][c] ? 1 : 0)) return false;
        }
    }
    return true;
}
